import { Rng } from '../../shared';
import type { EquipmentWeaponId, HeroBuild, WeaponAbilityId, WeaponTechniqueId } from '../../shared/equipment';

export const ACTIONS = ['strike', 'guard', 'feint'] as const;
export type DuelAction = (typeof ACTIONS)[number];
export type PlayerAction = DuelAction | 'prayer' | 'signature' | WeaponAbilityId;
export type OpponentId = 'novice' | 'knight' | 'prior' | 'collector';
export interface DuelSpec { seed: string; opponent: OpponentId; level: number; might?: number; grit?: number; cunning?: number; build?:HeroBuild }
export interface DuelBeat {
  round: number; action: PlayerAction; intent: DuelAction; dealt: number; received: number;
  healed: number; counter: boolean; heavy: boolean; joke: number;
  rivalPhase?: RivalPhase; rushStopped?: boolean; noReply?: boolean;
  armourRemaining?: number; armourBroken?: boolean; armourBlocked?: number;
  interrupted?: boolean; foeHealed?: number;
  /** Collector's non-damage receipt, applied only if both fighters survive. */
  fervorTaken?: number; levyEvaded?: boolean; levySuppressed?: boolean;
  weapon?:EquipmentWeaponId; enemyWeapon?:EquipmentWeaponId; shieldBlocked?:number;
  /** Technique ward and actual stamina restored, before any later turn. */
  techniqueBlocked?:number; staminaRestored?:number;
}
export interface DuelState {
  /** Missing on saved duels from the first release: those retain their original rules. */
  rulesVersion?: 1 | 2;
  spec: DuelSpec; round: number; hp: number; maxHp: number; foeHp: number; foeMaxHp: number;
  stamina: number; fervor: number; combo: number; counters: number; prayerUsed: boolean;
  intent: DuelAction; heavy: boolean; beats: DuelBeat[]; winner: 0 | 1 | null;
  shieldUsed?:boolean;
}
export const OPPONENTS: Record<OpponentId, { hp: number; attack: number; habit: DuelAction; species: string; bounty: 0.9 | 1 | 1.25 }> = {
  novice: { hp: 76, attack: 12, habit: 'strike', species: 'hare', bounty: 0.9 },
  knight: { hp: 108, attack: 17, habit: 'guard', species: 'cat', bounty: 1 },
  prior: { hp: 142, attack: 21, habit: 'feint', species: 'cat', bounty: 1.25 },
  collector: { hp: 128, attack: 16, habit: 'strike', species: 'fox', bounty: 1.25 },
};
export const COUNTER: Record<DuelAction, DuelAction> = { strike: 'guard', guard: 'feint', feint: 'strike' };
export const ACTION_COST: Record<DuelAction, number> = { strike: 2, guard: 0, feint: 1 };
const cap = (v: number, max: number): number => Math.max(0, Math.min(max, v));
const skill = (v: number | undefined): number => Number.isFinite(v) ? cap(Math.floor(v!), 12) : 0;
export const isSuperAction=(action:PlayerAction):boolean=>action==='signature'||action==='starfall'||action==='thousandCuts';
/** Combat semantics: intermediate techniques may receive a reply. The motion
 * module also calls their cinematic clips supers; do not conflate the two. */
export const TECHNIQUES:Record<WeaponTechniqueId,{weapon:EquipmentWeaponId;fervor:number;stamina:number}>={
  solarCleave:{weapon:'sword',fervor:45,stamina:1},seismicSlam:{weapon:'hammer',fervor:55,stamina:2},
  tempest:{weapon:'staff',fervor:50,stamina:0},shadowDance:{weapon:'daggers',fervor:45,stamina:0},
};
export const isTechniqueAction=(action:PlayerAction):action is WeaponTechniqueId=>Object.hasOwn(TECHNIQUES,action);
export const rivalWeapon=(id:OpponentId):EquipmentWeaponId=>id==='novice'?'daggers':id==='knight'?'hammer':id==='collector'?'sword':'staff';
export const maxStamina=(state:DuelState):number=>state.spec.build?.stats.maxStamina??4;

export const LEVY_FERVOR = 24;
export type RivalPhase = 'normal' | 'rush' | 'stumbled' | 'mail' | 'unfastened' | 'prepare' | 'chant' | 'audit' | 'levy';
export function rivalState(state: DuelState): { phase: RivalPhase; armour: number } {
  if (state.rulesVersion !== 2) return { phase: 'normal', armour: 0 };
  if (state.spec.opponent === 'collector') return { phase: state.round % 3 === 2 ? 'audit' : state.round % 3 === 0 ? 'levy' : 'normal', armour: 0 };
  if (state.spec.opponent === 'knight') {
    const armour = Math.max(0, 2 - state.beats.filter(beat => beat.armourBroken).length);
    return { phase: armour ? 'mail' : 'unfastened', armour };
  }
  if (state.spec.opponent === 'prior') return { phase: state.round % 4 === 2 ? 'prepare' : state.round % 4 === 3 ? 'chant' : 'normal', armour: 0 };
  if (state.spec.opponent !== 'novice') return { phase: 'normal', armour: 0 };
  const previous = state.beats.at(-1);
  if (previous?.rushStopped && previous.round === state.round - 1) return { phase: 'stumbled', armour: 0 };
  return { phase: state.round % 3 === 0 ? 'rush' : 'normal', armour: 0 };
}
function prepareRound(state: DuelState): void {
  const phase = rivalState(state).phase;
  state.intent = phase === 'rush' || phase === 'levy' ? 'strike' : phase === 'chant' ? 'feint' : intentFor(state.spec, state.round, state.rulesVersion);
  state.heavy = state.rulesVersion === 2 ? phase === 'rush' : state.round % 4 === 0;
}

function intentFor(spec: DuelSpec, round: number, rulesVersion?: 1 | 2): DuelAction {
  if (rulesVersion !== 2 && round % 4 === 0) return 'strike';
  const rng = new Rng(spec.seed).stream('intent', round);
  return rng.chance(0.55) ? OPPONENTS[spec.opponent].habit : rng.pick(ACTIONS);
}
export function createDuel(spec: DuelSpec): DuelState {
  const normalized: DuelSpec = { ...spec, ...(spec.build?{build:structuredClone(spec.build)}:{}),level: Math.max(1, Math.min(50, Math.floor(spec.level) || 1)) };
  const maxHp = normalized.build?.stats.maxHp??100 + skill(spec.grit) * 6 + (normalized.level - 1) * 4;
  const foeMaxHp = OPPONENTS[spec.opponent].hp + (normalized.level - 1) * 4;
  return { rulesVersion: 2, spec: normalized, round: 1, hp: maxHp, maxHp, foeHp: foeMaxHp, foeMaxHp,
    stamina: normalized.build?.stats.maxStamina??4, fervor: normalized.build?.stats.startFervor??20, combo: 0, counters: 0, prayerUsed: false,
    intent: intentFor(normalized, 1, 2), heavy: false, beats: [], winner: null };
}
export function canPlay(state: DuelState, action: PlayerAction): boolean {
  if (state.winner !== null) return false;
  if (action === 'prayer') return !state.prayerUsed && state.fervor >= 40 && state.hp < state.maxHp;
  if (action === 'signature') return !state.spec.build&&state.fervor >= 70;
  if(action==='starfall'||action==='thousandCuts')return !!state.spec.build?.abilities.includes(action)&&state.fervor>=70;
  if(isTechniqueAction(action)){const move=TECHNIQUES[action],build=state.spec.build;return !!build&&build.weapon===move.weapon&&build.abilities.includes(action)&&state.fervor>=move.fervor&&state.stamina>=move.stamina;}
  return ACTIONS.includes(action) && state.stamina >= ACTION_COST[action];
}

/** A turn is a pure, replayable decision. Unaffordable and post-result inputs are strict no-ops. */
export function playTurn(before: DuelState, action: PlayerAction): DuelState {
  if (!canPlay(before, action)) return before;
  const state = { ...before, beats: [...before.beats] };
  const rng = new Rng(state.spec.seed).stream('damage', state.round);
  const build=state.spec.build,effects=build?.effects;
  const attack = build?.stats.attack??17 + skill(state.spec.might) * 2 + (state.spec.level - 1);
  const enemyAttack = OPPONENTS[state.spec.opponent].attack + (state.spec.level - 1);
  const rival = rivalState(before), phase = rival.phase;
  const counter = phase !== 'stumbled' && (ACTIONS.includes(action as DuelAction) && COUNTER[state.intent] === action||action==='solarCleave'&&state.intent==='feint');
  let dealt = 0, received = 0, healed = 0, techniqueBlocked=0, staminaRestored=0;
  if (action === 'prayer') {
    healed = Math.min(state.maxHp - state.hp, Math.round(state.maxHp * 0.32)+(effects?.prayerBonus??0));
    state.hp += healed; state.fervor -= 40; state.prayerUsed = true;
    received = state.intent === 'guard' ? 0 : Math.round(enemyAttack * 0.65);
  } else if (action === 'signature'||action==='starfall'||action==='thousandCuts') {
    dealt = Math.round(attack * (action==='starfall'?2.35:2.1) + state.combo * (action==='starfall'?1:2)); state.fervor -= 70; state.stamina = Math.min(maxStamina(state), state.stamina + 1);
  } else if(isTechniqueAction(action)) {
    const move=TECHNIQUES[action],chain=Math.min(state.combo,3),incoming=enemyAttack*(state.heavy?1.65:1);
    state.fervor-=move.fervor;state.stamina-=move.stamina;
    if(action==='solarCleave'){
      dealt=Math.round(attack*1.45)+chain*2+(counter?(effects?.counterDamage??0):0);
      received=Math.round(incoming*(counter?.22:.65));
      if(counter){state.combo++;state.counters++;}
    }else if(action==='seismicSlam'){
      dealt=Math.round(attack*1.65);received=Math.round(incoming*.55);
    }else if(action==='tempest'){
      dealt=Math.round(attack*1.2);techniqueBlocked=Math.round(incoming);
      staminaRestored=Math.min(1,maxStamina(state)-state.stamina);
    }else{
      dealt=Math.round(attack*1.25)+chain;received=Math.round(incoming*.5);
      staminaRestored=Math.min(2,maxStamina(state)-state.stamina);
    }
    state.stamina+=staminaRestored;
    // Techniques preserve a chain but never generate fervor. This includes
    // Solar's successful counter: paid abilities cannot fund themselves.
  } else {
    state.stamina = cap(state.stamina - ACTION_COST[action] + (action === 'guard' ? 2 : 0), maxStamina(state));
    const advantage = phase === 'stumbled' ? 1 : counter ? 1.25 : action === state.intent ? 0.9 : 0.45;
    dealt = Math.round((attack + rng.int(-2, 3)) * advantage * (action === 'guard' ? 0.55 : 1) + (counter ? Math.min(state.combo, 3) * 2 : 0));
    received = Math.round((enemyAttack + rng.int(-2, 2)) * (counter ? 0.22 : action === state.intent ? 0.75 : 1.1) * (state.heavy ? 1.65 : 1));
    if(counter)dealt+=effects?.counterDamage??0;
    if (action === 'guard' && counter) received = Math.max(0, received - (build?.stats.guardReduction??2 + skill(state.spec.cunning)));
    else if(!build)received = Math.max(0, received - Math.floor(skill(state.spec.cunning) / 2));
    if(action==='guard'&&effects?.guardHeal){healed=Math.min(state.maxHp-state.hp,effects.guardHeal);state.hp+=healed;}
    state.combo = phase === 'stumbled' ? state.combo : counter ? state.combo + 1 : 0;
    state.counters += counter ? 1 : 0;
    state.fervor = cap(state.fervor + (counter ? 18+(effects?.counterFervor??0) : 9), 100);
  }
  const rushStopped = phase === 'rush' && (action === 'guard'||action==='tempest');
  const noReply = phase === 'stumbled' || phase === 'chant';
  if (phase === 'stumbled') dealt = Math.round(dealt * 1.25);
  if (noReply) received = 0;
  if (rushStopped) received = 0;
  const armourBroken = rival.armour > 0 && (action === 'feint'||action==='seismicSlam');
  const armourRemaining = rival.armour - (armourBroken ? 1 : 0);
  const armourBlocked = isSuperAction(action)||action==='seismicSlam' ? 0 : Math.min(dealt, armourRemaining * 4);
  dealt -= armourBlocked;
  state.foeHp = cap(state.foeHp - dealt, state.foeMaxHp);
  const interrupted = phase === 'chant' && (action === 'strike' || isSuperAction(action)||isTechniqueAction(action));
  const foeHealed = phase === 'chant' && !interrupted && state.foeHp > 0 ? Math.min(18, state.foeMaxHp - state.foeHp) : 0;
  state.foeHp += foeHealed;
  // A finishing blow ends the exchange; a defeated foe cannot answer it.
  if (state.foeHp === 0) received = 0;
  if(noReply||state.foeHp===0)techniqueBlocked=0;
  // Equipped armour protects every answer, including prayer and a successful
  // guard. Old duels retain their original mutually exclusive reductions.
  if(build)received=Math.max(0,received-build.stats.damageReduction);
  const shieldBlocked=!state.shieldUsed&&received>0?Math.min(received,effects?.firstHitShield??0):0;
  if(shieldBlocked){received-=shieldBlocked;state.shieldUsed=true;}
  state.hp = cap(state.hp - received, state.maxHp);
  // The seal is a published resource levy, not damage. Armour/Guard can stop
  // the blade but not the paperwork. Costs and ordinary fervor gains resolve
  // first; a dead combatant never pays or collects. Nothing carries as debt.
  const levyEvaded = phase === 'levy' && action === 'feint';
  const levySuppressed = phase === 'levy' && (isSuperAction(action) || action === 'tempest');
  const fervorTaken = phase === 'levy' && !levyEvaded && !levySuppressed && state.hp > 0 && state.foeHp > 0 ? Math.min(LEVY_FERVOR, state.fervor) : 0;
  state.fervor -= fervorTaken;
  const beat: DuelBeat = { round: state.round, action, intent: state.intent, dealt, received, healed, counter, heavy: state.heavy, joke: rng.int(0, 5) };
  if (state.rulesVersion === 2) Object.assign(beat, { rivalPhase: phase, rushStopped, noReply, armourRemaining, armourBroken, armourBlocked, interrupted, foeHealed });
  if (phase === 'levy') Object.assign(beat,{fervorTaken,levyEvaded,levySuppressed});
  if(build)Object.assign(beat,{weapon:build.weapon,enemyWeapon:rivalWeapon(state.spec.opponent),shieldBlocked});
  if(isTechniqueAction(action))Object.assign(beat,{techniqueBlocked,staminaRestored});
  state.beats.push(beat);
  if (state.foeHp === 0) state.winner = 0;
  else if (state.hp === 0) state.winner = 1;
  else if (state.round >= 24) state.winner = state.hp / state.maxHp >= state.foeHp / state.foeMaxHp ? 0 : 1;
  state.round++;
  prepareRound(state);
  return state;
}
export function autoAction(state: DuelState): PlayerAction {
  const phase = rivalState(state).phase;
  if (phase === 'rush') return 'guard';
  const abilities=state.spec.build?.abilities??[];
  const ultimate=abilities.find(a=>!isTechniqueAction(a)&&canPlay(state,a));if(ultimate)return ultimate;
  if (canPlay(state, 'signature')) return 'signature';
  if (phase === 'levy' && state.fervor >= 52 && state.hp > state.maxHp * .5 && canPlay(state,'feint')) return 'feint';
  if (phase === 'chant' && canPlay(state, 'strike')) return 'strike';
  if (phase === 'prepare' && state.stamina < 2) return 'guard';
  if (state.hp < state.maxHp * 0.45 && canPlay(state, 'prayer')) return 'prayer';
  const technique=abilities.find(a=>isTechniqueAction(a)&&canPlay(state,a)&&
    (a==='solarCleave'&&state.intent==='feint'||a==='seismicSlam'&&phase==='mail'||
     a==='tempest'&&state.intent==='strike'||a==='shadowDance'&&state.stamina<=maxStamina(state)-2));
  if(technique)return technique;
  if (phase === 'stumbled' && canPlay(state, 'strike')) return 'strike';
  if (phase === 'mail' && canPlay(state, 'feint')) return 'feint';
  const action = COUNTER[state.intent];
  return canPlay(state, action) ? action : 'guard';
}
export function runDuel(spec: DuelSpec): DuelState {
  let state = createDuel(spec);
  while (state.winner === null) state = playTurn(state, autoAction(state));
  return state;
}
